import { CurrentClientUserDependencyTypes } from '@client/entity/current-client-user/current-client-user.dependency-types';
import { container } from '@shared/common/dependency-injection/domain/container';
import type { CurrentClientUserService } from '@client/entity/current-client-user/domain/service/current-client-user.service';
import { useCurrentClientUserStore } from '@client/entity/current-client-user/application/store/current-client-user.store';
import { CurrentClientUserLoadError } from '@client/entity/current-client-user/domain/error/current-client-user-load.error';
import { NetworkDependencyTypes } from '@shared/common/network/network.dependency-types';
import type { JwtRepositoryInterface } from '@shared/common/network/domain/repository/jwt.repository';
import { LoginRouteName } from '@shared/feature/login/application/routes/login.route-name';
import type { RouteLocationRaw } from 'vue-router';
import { CurrentUserIsNotAClientError } from '@client/entity/current-client-user/domain/error/current-user-is-not-a-client.error';
import { ToastDependencyTypes, type ToastService } from '@kiplin/web-common/toast';
import { LOGIN_TOAST_OPTIONS } from '@shared/feature/login/domain/constant/login-toast.constant';
import { useMainLayoutStore } from '@client/composition/main-layout/application/store/main-layout.store';
import { TranslationDependencyTypes } from '@kiplin/web-common/translation';
import type { Composer } from 'vue-i18n';

export async function checkRootDataAreFetched(): Promise<boolean | RouteLocationRaw> {
  const currentClientUserStore = useCurrentClientUserStore();
  if (currentClientUserStore.currentClientUser) {
    return true;
  }

  const currentClientUserService = container.get<CurrentClientUserService>(
    CurrentClientUserDependencyTypes.CurrentClientUserService,
  );

  currentClientUserStore.setLoading(true);

  try {
    const user = await currentClientUserService.loadCurrentClientUser();
    currentClientUserStore.setCurrentClientUser(user);
    currentClientUserStore.setError(null);
    return true;
  } catch (error) {
    const loadError = new CurrentClientUserLoadError(error);
    // eslint-disable-next-line no-console
    console.error(loadError);

    const jwtRepository = container.get<JwtRepositoryInterface>(NetworkDependencyTypes.JwtRepository);
    const toastService = container.get<ToastService>(ToastDependencyTypes.ToastService);
    const i18n = container.get<Composer>(TranslationDependencyTypes.i18n);
    let feedbackMessageKey: string;

    switch (true) {
      case error instanceof CurrentUserIsNotAClientError:
        feedbackMessageKey = 'login.wrong-space.funder';
        break;
      default:
        feedbackMessageKey = 'login.feedback-reconnect-required';
    }

    jwtRepository.clearToken();
    currentClientUserStore.reset();
    useMainLayoutStore().reset();
    toastService.showError(i18n.t(feedbackMessageKey), undefined, LOGIN_TOAST_OPTIONS);

    return { name: LoginRouteName.Login };
  } finally {
    currentClientUserStore.setLoading(false);
  }
}
